import { useEffect, useCallback, useRef } from 'react'

type KeyHandler = (event: KeyboardEvent) => void

interface KeyboardShortcutOptions {
  preventDefault?: boolean
  enabled?: boolean
  ignoreElements?: string[]
}

interface ShortcutMap {
  [keyCombo: string]: KeyHandler
}

const useKeyboardShortcuts = (
  shortcuts: ShortcutMap,
  options: KeyboardShortcutOptions = {}
): void => {
  const {
    preventDefault = false,
    enabled = true,
    ignoreElements = ['input', 'textarea', 'select'],
  } = options

  // Use refs to store current values without causing re-renders
  const shortcutsRef = useRef(shortcuts)
  const enabledRef = useRef(enabled)
  const preventDefaultRef = useRef(preventDefault)
  const ignoreElementsRef = useRef(ignoreElements)

  // Update refs when values change
  shortcutsRef.current = shortcuts
  enabledRef.current = enabled
  preventDefaultRef.current = preventDefault
  ignoreElementsRef.current = ignoreElements

  const handleKeyDown = useCallback((event: KeyboardEvent): void => {
    if (!enabledRef.current) return

    const target = event.target as HTMLElement
    if (
      target &&
      ignoreElementsRef.current.includes(target.tagName.toLowerCase())
    ) {
      return
    }

    if (target && target.getAttribute('contenteditable') === 'true') {
      return
    }

    let keyCombo = ''
    if (event.ctrlKey) keyCombo += 'ctrl+'
    if (event.altKey) keyCombo += 'alt+'
    if (event.shiftKey) keyCombo += 'shift+'
    if (event.metaKey) keyCombo += 'meta+' // Command key on Mac
    keyCombo += event.key.toLowerCase()

    const handler = shortcutsRef.current[keyCombo]
    if (handler) {
      event.preventDefault()
      handler(event)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])
}

export default useKeyboardShortcuts
